import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardContent, FormHelperText, Grid, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { NOSPACE_REGEX } from "../../../utils/constants";
import errorHandler from "../../../utils/errorHandler";
import EdiorJodit from "../../../components/editor/editorJodit";
import { toast } from "react-toastify";
import { apiCms } from "../../../services/AllServices";

const editorContentValidation = (cmsData, context) => {
  if (cmsData === undefined || cmsData === null) {
    return context.createError({ message: "Answer is required" });
  }

  const plainText = cmsData
    .replace(/<[^>]*>/g, "")   // Remove HTML tags
    .replace(/&nbsp;/gi, " ")  // Convert &nbsp; to spaces
    .trim();

  if (!plainText) {
    return context.createError({ message: "Answer is required" });
  }

  if (/^\s|^(&nbsp;)+/i.test(cmsData.replace(/<[^>]*>/g, ""))) {
    return context.createError({ message: "Answer should not start with a space" });
  }

  if (plainText.length < 10) {
    return context.createError({ message: "Answer must be at least 10 characters long" });
  }

  if (plainText.length > 1000) {
    return context.createError({ message: "Answer must not be more than 1000 characters." });
  }

  return true;
};


const validationSchema = Yup.object().shape({
  question: Yup.string()
    .required("Question is required")
    .matches(NOSPACE_REGEX, "Spaces at start & end is not allowed")
    .min(2, "Please enter at least 2 characters")
    .max(100, "Question must not be more than 100 characters.")
    .matches(/^(?=.*[a-zA-Z])[a-zA-Z0-9 _\-@()?!.,'"[\]{}<>\\/|:;`~#$%^&+=*]*$/, "Question must contain at least one alphabet character and can include digits or special characters")
,
  status: Yup.mixed().required("Status is required"),
  answer: Yup.string().test(
    "custom-answer-validation",
    "Answer is required",
    function (value) {
      return editorContentValidation(value, this);
    }
  ),

});

export default function AddEditFaq() {
  const navigate = useNavigate();
  const { id } = useParams();

  const formik = useFormik({
    initialValues: { question: "", answer: "", status: "" },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      console.log(values, "Sdfasd")
      const apiCall = id
        ? apiCms.editFaq({
          id: id,
          question: values.question,
          answer: values.answer,
          status: values.status,
        })
        : apiCms.addFaq({
          question: values.question,
          answer: values.answer,
          status: values.status,
        });
      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/faq");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

  useEffect(() => {
    if (id) {
      apiCms
        .getByIdFaq({ id: id })
        .then((res) => {
          const data = res?.data?.data;
          formik.setValues({
            question: data.question,
            status: data.status,
            answer: data.answer, // Set the answer directly in Formik values
          });
        })
        .catch((err) => {
          errorHandler(err);
        });
    }
  }, [id]);
  return (
    <>
      <Card>
        <CardContent>
          <FormikProvider value={formik} style={{ padding: "34px 30px" }}>
            <Typography variant="h4" sx={{ mb: 2 }} align="start">
              {id ? "Edit" : "Add"} FAQ
            </Typography>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  name="question"
                  label="Question"
                  {...getFieldProps("question")}
                  error={Boolean(touched.question && errors.question)}
                  helperText={touched.question && errors.question}
                />

                <EdiorJodit
                  onChange={(html) => {
                    setFieldValue("answer", html);
                  }}
                  onBlur={() => formik.setFieldTouched("answer", true)}
                  data={formik.values.answer}
                  placeholder="Answer"
                />

                {formik.touched.answer && formik.errors.answer && (
                  <FormHelperText error sx={{ display: 'block' }}>
                    {formik.errors.answer}
                  </FormHelperText>
                )}

                <Grid item xs={12}>
                  <Select
                    labelId="status-label"
                    id="status"
                    value={formik.values.status}
                    onChange={(e) => formik.setFieldValue("status", e.target.value)}
                    fullWidth
                    error={Boolean(formik.touched.status && formik.errors.status)}
                    displayEmpty
                    defaultValue=""
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <FormHelperText error>{formik.errors.status}</FormHelperText>
                  )}
                </Grid>
              </Stack>
              <Stack
                spacing={2}
                mt={3}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  size="large"
                  type="button" // Change from "submit" to "button"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ my: 2 }}
                  onClick={handleSubmit} // Manually trigger form submission
                >
                  {id ? "Update" : "Add"}
                </Button>

                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    if (!isSubmitting) navigate("/faq");
                  }}
                  sx={{ my: 2, ml: 4 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </>
  );
}
